import { Component, input, output, signal, ElementRef, ViewChild, AfterViewInit, HostListener, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Placement } from '../types';

@Component({
  selector: 'app-popover',
  imports: [CommonModule],
  template: `
    <div class="relative inline-block" #triggerElement>
      <ng-content select="[trigger]"></ng-content>
      
      @if (visible()) {
        <div
          #popoverElement
          [class]="popoverClasses"
          [ngStyle]="popoverStyles()"
          (click)="$event.stopPropagation()"
          (mousedown)="$event.stopPropagation()">
          @if (header()) {
            <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ header() }}</h3>
            </div>
          }
          <div [class]="contentClasses">
            <ng-content></ng-content>
          </div>
        </div>
      }
    </div>
  `,
  styles: []
})
export class PopoverComponent implements AfterViewInit {
  visible = input<boolean>(false);
  header = input<string>('');
  placement = input<Placement>('bottom');
  dismissable = input<boolean>(true);
  width = input<string>('auto');

  visibleChange = output<boolean>();

  @ViewChild('triggerElement', { static: false }) triggerElement!: ElementRef<HTMLElement>;
  @ViewChild('popoverElement', { static: false }) popoverElement!: ElementRef<HTMLElement>;

  popoverStyles = signal<{ [key: string]: string }>({});

  ngAfterViewInit(): void {
    effect(() => {
      if (this.visible()) {
        setTimeout(() => this.updatePosition(), 0);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.visible() || !this.dismissable()) return;
    
    const target = event.target as HTMLElement;
    if (this.popoverElement?.nativeElement?.contains(target)) return;
    if (this.triggerElement?.nativeElement?.contains(target)) return;
    
    this.visibleChange.emit(false);
  }

  updatePosition(): void {
    if (!this.triggerElement || !this.popoverElement) return;
    
    const triggerRect = this.triggerElement.nativeElement.getBoundingClientRect();
    const popoverRect = this.popoverElement.nativeElement.getBoundingClientRect();
    const gap = 8;

    let top = 0;
    let left = 0;

    switch (this.placement()) {
      case 'top':
        top = triggerRect.top - popoverRect.height - gap;
        left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
        left = triggerRect.left - popoverRect.width - gap;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
        left = triggerRect.right + gap;
        break;
      case 'top-start':
        top = triggerRect.top - popoverRect.height - gap;
        left = triggerRect.left;
        break;
      case 'top-end':
        top = triggerRect.top - popoverRect.height - gap;
        left = triggerRect.right - popoverRect.width;
        break;
      case 'bottom-start':
        top = triggerRect.bottom + gap;
        left = triggerRect.left;
        break;
      case 'bottom-end':
        top = triggerRect.bottom + gap;
        left = triggerRect.right - popoverRect.width;
        break;
      default:
        top = triggerRect.bottom + gap;
        left = triggerRect.left;
    }

    // Viewport adjustments
    if (left < 8) left = 8;
    if (left + popoverRect.width > window.innerWidth - 8) {
      left = window.innerWidth - popoverRect.width - 8;
    }
    if (top < 8) top = 8;
    if (top + popoverRect.height > window.innerHeight - 8) {
      top = window.innerHeight - popoverRect.height - 8;
    }

    this.popoverStyles.set({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: '9999'
    });
  }

  get popoverClasses(): string {
    const classes = [
      'absolute',
      'bg-white',
      'dark:bg-gray-800',
      'rounded-lg',
      'shadow-xl',
      'border',
      'border-gray-200',
      'dark:border-gray-700',
      'z-50'
    ];

    if (this.width() !== 'auto') {
      classes.push(`w-${this.width()}`);
    } else {
      classes.push('min-w-[200px]', 'max-w-sm');
    }

    return classes.join(' ');
  }

  get contentClasses(): string {
    return 'px-4 py-3';
  }
}

